import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { contentApi } from '../../services/contentApi';
import { useAuth } from '../../context/AuthContext';
import Card from '../../components/UI/Card';
import CourseRating from '../../components/content/CourseRating';

type EarningCourse = {
  id: number;
  title: string;
  slug: string;
  status: string;
  isPublic: boolean;
  averageRating: number;
  ratingCount: number;
  enrollments: number;
  revenue: number;
};

const CourseOwnerPanel: React.FC = () => {
  const { user } = useAuth();
  const [collected, setCollected] = useState(0);
  const [courses, setCourses] = useState<EarningCourse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const percent = Number(user?.courseOwnerRevenuePercent || 0);
  const share = Math.round(collected * percent) / 100;

  useEffect(() => {
    contentApi.courseEarnings()
      .then((r) => {
        setCollected(Number(r.data.collected || 0));
        setCourses(r.data.courses || []);
      })
      .catch(() => setError('Could not load course earnings.'));
  }, []);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">Your courses, enrollments, fees collected, and the revenue share set when you were invited.</p>
      {error && <p className="text-sm text-error-600">{error}</p>}
      <div className="grid sm:grid-cols-3 gap-3">
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Fees collected</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">₹{collected.toLocaleString('en-IN')}</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Your percentage</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{percent}%</p>
        </Card>
        <Card className="p-4">
          <p className="text-xs uppercase tracking-wide text-gray-500">Your share</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">₹{share.toLocaleString('en-IN')}</p>
        </Card>
      </div>
      <div className="space-y-2">
        {courses.length === 0 && <p className="text-sm text-gray-500">You have not created a course yet.</p>}
        {courses.map((course) => (
          <div key={course.id} className="rounded-xl border bg-white p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <Link to={`/my-courses/${course.id}/edit`} className="font-semibold text-gray-900">{course.title}</Link>
              <p className="text-sm text-gray-600">
                {course.status} · {course.isPublic ? 'Public' : 'Private'} · {course.enrollments} enrollments · ₹{Number(course.revenue || 0).toLocaleString('en-IN')} collected
              </p>
              <CourseRating average={course.averageRating} count={course.ratingCount} />
            </div>
            {course.status === 'Published' && (
              <Link to="/admin/dashboard?tab=learners" className="text-sm font-semibold text-primary-700">Invite learners</Link>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CourseOwnerPanel;
